import React from 'react';
import useSWR from 'swr';
import api, {getURL, ResolverTypes} from '../api';
import {IS_UNIVERSAL_SESSION_PARAM} from '../App';
import {useStoredURLParams} from './storedURLParams';
import {useAuth} from './auth';
import {Reservation} from '../utils/types';

type ContextProps = {
  data: any;
  isLoading: boolean;
  isError: boolean;
  setReservation: React.Dispatch<React.SetStateAction<any>>;
  refreshReservation: (beforeUpdateCallback?: () => void) => Promise<ResolverTypes>;
  patchReservation: (
    payload: any,
    beforeUpdateCallback?: () => void,
  ) => Promise<ResolverTypes>;
};

const ReservationContext = React.createContext<ContextProps>({
  data: {},
  isLoading: false,
  isError: false,
  setReservation: () => {},
  refreshReservation: (beforeUpdateCallback?: () => void) =>
    Promise.resolve({error: null, data: null}),
  patchReservation: () => Promise.resolve({error: null, data: null}),
});

function ReservationProvider(props: any) {
  const {reservationId} = useStoredURLParams();
  const {isLoading: isLoadingToken, isTokenValid} = useAuth();
  const isUniversalSession = sessionStorage.getItem(IS_UNIVERSAL_SESSION_PARAM);

  const {
    data,
    mutate,
    error,
    isValidating: isLoading,
  } = useSWR<Reservation>(getURL(api.reservations.ENDPOINTS.getOne(reservationId)), {
    revalidateOnMount: false,
  });
  const reservation = React.useMemo<Partial<Reservation>>(() => data || {}, [data]);
  const hasReservation = !!reservation.id;
  const isError = error;

  const refreshReservation = React.useCallback(
    async (beforeUpdateCallback?: () => void) => {
      beforeUpdateCallback?.();
      return await mutate();
    },
    [mutate],
  );

  const setCacheReservation = React.useCallback(
    (callback: (prevValue: Partial<Reservation>) => Reservation) => {
      const newState = callback(reservation);
      mutate(newState, false);
    },
    [mutate, reservation],
  );

  const patchReservation = React.useCallback(
    (id = reservationId, payload: Partial<Reservation>) => {
      return api.reservations.patch(id, payload);
    },
    [reservationId],
  );

  const patchAndSetReservation = React.useCallback(
    async (payload: any = {}, beforeUpdateCallback?: () => void) => {
      if (!reservationId) {
        return;
      }
      const newState = {...reservation, ...payload};
      mutate(newState, false);

      const {data, error} = await patchReservation(reservationId, payload);
      beforeUpdateCallback?.();
      mutate();

      return {data, error};
    },
    [mutate, patchReservation, reservation, reservationId],
  );

  React.useEffect(
    function preloadReservation() {
      const canFetchReservation =
        !hasReservation &&
        !isLoadingToken &&
        isTokenValid &&
        !isUniversalSession &&
        reservationId &&
        !error &&
        !isLoading;

      if (canFetchReservation) {
        mutate();
      }
    },
    [
      error,
      isLoading,
      isLoadingToken,
      isTokenValid,
        isUniversalSession,
      mutate,
      hasReservation,
      reservationId,
    ],
  );

  return (
    <ReservationContext.Provider
      {...props}
      value={{
        isLoading,
        isError,
        setReservation: setCacheReservation,
        patchReservation: patchAndSetReservation,
        refreshReservation,
        data: reservation,
      }}
    />
  );
}

function useReservation() {
  const context = React.useContext(ReservationContext);
  if (context === undefined) {
    throw new Error('useReservation must be used within a ReservationProvider');
  }
  return context;
}

export {ReservationContext, ReservationProvider, useReservation};
